
import type {
  RelatedTags,
  RelatedTagsIndexed,
  RelatedTagsRules,
  RelatedTagsRulesItem,
} from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { RelatedTagConfig, ValidityState } from '../../models'
import Sortable from 'sortablejs'
import { computed, defineComponent, nextTick, onBeforeUpdate, onUpdated, ref, watch } from 'vue'
import { RelatedTagPositionEnum } from '../../enums'
import { ValidityStateTypeEnum } from '../../models'

import EpbLegend from '../legend.component.vue'
import { EpbLoading } from '../shared'
import { EpbButton } from '../shared/epb-button'
import { EpbSwitch } from '../shared/epb-switch'
import { EpbText } from '../shared/epb-text'
import { EpbToggle } from '../shared/epb-toggle'
import RelatedTagComponent from './related-tag.component.vue'

export default defineComponent({
  name: 'RelatedTagsConfig',
  components: {
    EpbButton,
    EpbSwitch,
    EpbText,
    EpbToggle,
    RelatedTag: RelatedTagComponent,
    EpbLegend,
    EpbLoading,
  },
  props: {
    value: {
      type: Object as PropType<RelatedTags>,
      required: true,
    },
    readOnly: Boolean,
    allowDisableAllIndexed: Boolean,
    messages: {
      type: Object,
      required: true,
    },
    areRelatedTagsLoading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const baseClass = 'epb-related-tags-config'
    const formTagConfigEl = ref(null)
    const listEl = ref(null)
    const hideRemovedTags = ref(false)
    const formExpanded = ref(false)
    const formTagConfig = ref<RelatedTagConfig>({
      tag: '',
      tagPosition: RelatedTagPositionEnum.BEFORE_QUERY,
    })

    const pillsListSortableObject = ref(undefined)

    const legendItems = [
      {
        label: props.messages.fields.rules.columns.added.label,
        icon: 'usercirclesolid',
        color: 'primary',
      },
    ]

    const newTag = ref('')

    const hideRemovedTagsActionMessage = computed<string>(() => {
      const actionMessages = props.messages.fields.rules.actions.remove
      return hideRemovedTags.value ? actionMessages.show : actionMessages.hide
    })

    const tagPositionOptions = computed<Array<Record<string, any>>>(() => {
      return Object.values(RelatedTagPositionEnum).map(
        (relatedTagPosition: RelatedTagPositionEnum) => ({
          id: relatedTagPosition,
          content: props.messages.fields.rules.tag.position[relatedTagPosition],
        }),
      )
    })

    const query = computed<string>(() => {
      return props.value.criteria.queries[0] ?? ''
    })

    const tagConfigs = computed<Array<RelatedTagConfig>>(() => {
      return [
        ...getRulesTagConfigs(props.value.data.rules),
        ...getIndexedTagConfigs(props.value.data.indexed),
      ]
    })

    const visibleTagConfigs = computed<Array<RelatedTagConfig>>(() => {
      let visibleTagConfigs = tagConfigs.value
      if (hideRemovedTags.value) {
        visibleTagConfigs = visibleTagConfigs.filter(
          (tagConfig: RelatedTagConfig) => tagConfig.rule?.type !== 'REMOVE',
        )
      }
      visibleTagConfigs.forEach((tagConfig: RelatedTagConfig) => {
        const tagQuery = getTagConfigQuery(tagConfig)
        tagConfig.validityState = getRuleValidityState(
          {
            id: undefined,
            query: tagQuery,
            tag: tagConfig.tag,
            type: tagConfig?.rule?.type,
          },
          true,
        )
        tagConfig.draggable =
          !props.readOnly && (tagConfig?.rule?.type === 'ADD' || tagConfig?.rule === undefined)
      })
      return visibleTagConfigs.sort(
        (tagConfig1: RelatedTagConfig, tagConfig2: RelatedTagConfig) => {
          const ruleType1 = tagConfig1.rule ? tagConfig1.rule.type : undefined
          const ruleType2 = tagConfig2.rule ? tagConfig2.rule.type : undefined
          if (ruleType1 !== ruleType2) {
            if (ruleType1 === 'ADD' || ruleType2 === 'REMOVE') {
              return -1
            }
            if (ruleType1 === 'REMOVE' || ruleType2 === 'ADD') {
              return 1
            }
          }
          return 0
        },
      )
    })

    const areAllIndexedDisabled = computed<boolean>(() => {
      return props.value.data.indexed.every(indexed =>
        hasRule(indexed.query, props.value.data.rules),
      )
    })

    function hasRule(query: string, rules: RelatedTagsRules): boolean {
      return rules.some(rule => rule.query === query)
    }

    function disableAllIndexed(): void {
      let rules = [...props.value.data.rules]
      if (!areAllIndexedDisabled.value) {
        props.value.data.indexed.forEach(indexed => {
          if (!hasRule(indexed.query, rules)) {
            rules.push({
              ...indexed,
              id: undefined,
              type: 'REMOVE',
            })
          }
        })
      } else {
        rules = rules.filter(rule => rule.type !== 'REMOVE')
      }
      updateRules(rules)
    }

    function getRulesTagConfigs(rules: RelatedTagsRules): Array<RelatedTagConfig> {
      return rules.reduce((tagConfigs: Array<RelatedTagConfig>, rule) => {
        if (rule.type === 'ADD' || rule.type === 'REMOVE') {
          tagConfigs.push({
            tag: rule.tag,
            tagPosition: getTagPosition(rule.query, rule.tag),
            rule: {
              id: rule.id,
              type: rule.type,
            },
          })
        }
        return tagConfigs
      }, [])
    }

    function getIndexedTagConfigs(indexedTags: RelatedTagsIndexed): Array<RelatedTagConfig> {
      return indexedTags.reduce((tagConfigs: Array<RelatedTagConfig>, indexedTag) => {
        const tagRule = props.value.data.rules.find(rule => rule.query === indexedTag.query)
        if (!tagRule || (tagRule.type !== 'ADD' && tagRule.type !== 'REMOVE')) {
          tagConfigs.push({
            tag: indexedTag.tag,
            tagPosition: getTagPosition(indexedTag.query, indexedTag.tag),
            ...(!!tagRule && {
              rule: {
                id: tagRule.id,
                type: tagRule.type,
              },
            }),
          })
        }
        return tagConfigs
      }, [])
    }

    function getTagPosition(tagQuery: string, tag: string): RelatedTagPositionEnum {
      if (tagQuery.startsWith(`${tag} `)) {
        return RelatedTagPositionEnum.BEFORE_QUERY
      }
      return RelatedTagPositionEnum.AFTER_QUERY
    }

    function getTagConfigQuery(tagConfig: RelatedTagConfig): string {
      const tagQueryArray = [tagConfig.tag, query.value]
      if (tagConfig.tagPosition === RelatedTagPositionEnum.AFTER_QUERY) {
        tagQueryArray.reverse()
      }
      return tagQueryArray.join(' ')
    }

    function onExpandFormClick(): void {
      setFormExpanded(!formExpanded.value)
    }

    function setFormExpanded(expanded: boolean): void {
      if (expanded) {
        formTagConfig.value = {
          ...formTagConfig.value,
          tag: newTag.value,
          rule: {
            type: 'ADD',
          },
        }
        const tagQuery = getTagConfigQuery(formTagConfig.value)
        formTagConfig.value.validityState = getRuleValidityState({
          id: undefined,
          query: tagQuery,
          tag: formTagConfig.value.tag,
          type: formTagConfig.value.rule.type,
        })
      }
      formExpanded.value = expanded
    }

    interface Style {
      x: number
      opacity: number
    }

    const visibleTagConfigStyle: Style = { x: 0, opacity: 1 }
    const hiddenTagConfigStyle: Style = { x: 20, opacity: 0 }

    function setTagConfigAnimationStylesOnEnter(element: HTMLElement): void {
      animateTagConfigElement(element, hiddenTagConfigStyle, visibleTagConfigStyle)
    }

    function setTagConfigAnimationStylesOnLeave(element: HTMLElement): void {
      animateTagConfigElement(element, visibleTagConfigStyle, hiddenTagConfigStyle)
    }

    function animateTagConfigElement(element: HTMLElement, from: Style, to: Style) {
      element.style.transform = `translate3d(${from.x}px, 0, 0)`
      element.style.opacity = `${from.opacity}`
      requestAnimationFrame(() => {
        element.style.transform = `translate3d(${to.x}px, 0, 0)`
        element.style.opacity = `${to.opacity}`
      })
    }

    function removeTagConfigAnimationStyles(element: HTMLElement): void {
      ;['position', 'top', 'left', 'transform', 'opacity'].forEach((propertyName: string) =>
        element.style.removeProperty(propertyName),
      )
    }

    function onTagConfigEdit(tag: string): void {
      formTagConfig.value.tag = tag.trim()
      const tagQuery = getTagConfigQuery(formTagConfig.value)
      formTagConfig.value.validityState = getRuleValidityState({
        id: undefined,
        query: tagQuery,
        tag: formTagConfig.value.tag,
        type: formTagConfig.value.rule.type,
      })
      if (
        !!formTagConfig.value.tag &&
        !props.value.data.rules.some(
          rule => rule.query.toLowerCase() === tagQuery.toLowerCase() && rule.type === 'ADD',
        )
      ) {
        const indexRemoveTag = props.value.data.rules.findIndex(
          rule => rule.query === tagQuery && rule.type === 'REMOVE',
        )
        if (indexRemoveTag >= 0) {
          props.value.data.rules.splice(indexRemoveTag, 1)
        }
        emit('input', {
          ...props.value,
          data: {
            ...props.value.data,
            rules: [
              {
                id: undefined,
                query: tagQuery,
                tag: formTagConfig.value.tag,
                validityState: formTagConfig.value.validityState,
                type: formTagConfig.value.rule.type,
              },
              ...props.value.data.rules,
            ],
          },
        })
        newTag.value = ''
      }
    }

    function getRuleValidityState(
      rule: RelatedTagsRulesItem,
      initValidation = false,
    ): ValidityState {
      if (!rule.tag) {
        return {
          type: ValidityStateTypeEnum.VALID,
        }
      }
      const validationMessages = props.messages.fields.rules.validations
      if (isRuleMatchingRuled(rule) && !initValidation) {
        return {
          type: ValidityStateTypeEnum.ERROR,
          message: validationMessages.duplicated,
        }
      }
      if ((isRuleMatchingIndexed(rule) && !initValidation) || isRuleMatchingRuledAndIndexed(rule)) {
        return {
          type: ValidityStateTypeEnum.WARNING,
          message: validationMessages.matchesIndexed,
        }
      }
      return {
        type: ValidityStateTypeEnum.VALID,
      }
    }

    function isRuleMatchingRuled(rule: RelatedTagsRulesItem): boolean {
      const rules = getRulesTagConfigs(props.value.data.rules)
      return rules.some(
        r =>
          r.tag === rule.tag &&
          r.tagPosition === getTagPosition(rule.query, rule.tag) &&
          r.rule.type === 'ADD',
      )
    }

    function isRuleMatchingIndexed(rule: RelatedTagsRulesItem): boolean {
      return props.value.data.indexed.some(indexed => {
        return (
          indexed.tag === rule.tag &&
          getTagPosition(indexed.query, indexed.tag) === getTagPosition(rule.query, rule.tag)
        )
      })
    }

    function isRuleMatchingRuledAndIndexed(rule: RelatedTagsRulesItem): boolean {
      return isRuleMatchingIndexed(rule) && isRuleMatchingRuled(rule)
    }

    function onTagConfigToggleRemoved(tagConfig: RelatedTagConfig): void {
      const tagQuery = getTagConfigQuery(tagConfig)
      const tagRule = props.value.data.rules.find(rule => rule.query === tagQuery)
      const rules = [...props.value.data.rules]
      if (!tagRule) {
        rules.push({
          id: undefined,
          query: tagQuery,
          tag: tagConfig.tag,
          type: 'REMOVE',
        })
      } else {
        rules.splice(rules.indexOf(tagRule), 1)
      }
      emit('input', {
        ...props.value,
        data: {
          ...props.value.data,
          rules,
        },
      })
    }

    watch(formExpanded, (expanded: boolean): void => {
      if (expanded) {
        nextTick(() => {
          formTagConfigEl.value.focus()
        })
      }
    })

    watch(
      () => formTagConfig.value.tagPosition,
      (): void => {
        if (formExpanded.value) {
          nextTick(() => {
            formTagConfig.value.tag = newTag.value
            const tagQuery = getTagConfigQuery(formTagConfig.value)
            formTagConfig.value.tag = ''
            formTagConfig.value.validityState = getRuleValidityState({
              id: undefined,
              query: tagQuery,
              tag: newTag.value,
              type: formTagConfig.value.rule.type,
            })
            formTagConfigEl.value.focus()
          })
        }
      },
    )

    function updateRules(rules: RelatedTagsRules): void {
      emit('input', {
        ...props.value,
        data: {
          ...props.value.data,
          rules,
        },
      })
    }

    function getValidateState(event): void {
      let tagQuery = getTagConfigQuery(formTagConfig.value)
      if (newTag.value !== formTagConfig.value.tag || !newTag.value.length) {
        newTag.value = event.key === 'Enter' && newTag.value === '' ? '' : event.target.innerText
        if (formTagConfig.value.tag !== newTag.value) {
          formTagConfig.value.tag = newTag.value
          tagQuery = getTagConfigQuery(formTagConfig.value)
          formTagConfig.value.tag = ''
        }
      } else {
        newTag.value = event.key === 'Enter' && newTag.value === '' ? '' : event.target.innerText
        formTagConfig.value.tag = newTag.value
      }
      if (event.key !== 'Enter') {
        formTagConfig.value.validityState = getRuleValidityState({
          id: undefined,
          query: tagQuery,
          tag: newTag.value,
          type: formTagConfig.value.rule.type,
        })
      }
    }

    function hasConvertPreviousPillToCurated(
      index: number,
      newIndex: number,
      oldIndex: number,
    ): boolean {
      return (index < newIndex && index !== oldIndex) || (index === newIndex && index >= oldIndex)
    }

    onUpdated((): void => {
      if (listEl.value?.$el) {
        pillsListSortableObject.value = Sortable.create(listEl.value.$el, {
          animation: 500,
          draggable: `.${baseClass}__item--draggable`,
          onUpdate: event => {
            const { oldIndex, newIndex } = event
            const draggedQuery = visibleTagConfigs.value[oldIndex].tag
            const rules = [...props.value.data.rules]
            visibleTagConfigs.value.forEach((tag, index) => {
              if (
                tag?.rule === undefined &&
                (oldIndex === index || hasConvertPreviousPillToCurated(index, newIndex, oldIndex))
              ) {
                const tagQuery = getTagConfigQuery(tag)
                const rule: RelatedTagsRulesItem = {
                  id: undefined,
                  query: tagQuery,
                  tag: tag.tag.trim(),
                  type: 'ADD',
                }
                rules.splice(newIndex, 0, rule)
              } else if (oldIndex !== index) {
                const draggedRuleIndex = rules.findIndex(rule => {
                  return (
                    rule.tag === draggedQuery &&
                    getTagPosition(rule.query, rule.tag) ===
                      visibleTagConfigs.value[oldIndex].tagPosition
                  )
                })
                if (draggedRuleIndex >= 0) {
                  rules.splice(newIndex, 0, rules.splice(draggedRuleIndex, 1)[0])
                }
              }
            })
            rules.splice(
              newIndex,
              0,
              rules.splice(
                rules.findIndex(
                  rule =>
                    rule.tag === draggedQuery &&
                    getTagPosition(rule.query, rule.tag) ===
                      visibleTagConfigs.value[oldIndex].tagPosition,
                ),
                1,
              )[0],
            )
            updateRules(rules)
          },
        })
      }
    })

    onBeforeUpdate((): void => {
      if (pillsListSortableObject.value?.el) {
        pillsListSortableObject.value.destroy()
      }
    })

    return {
      baseClass,
      formTagConfigEl,
      listEl,
      hideRemovedTags,
      formExpanded,
      formTagConfig,
      legendItems,
      hideRemovedTagsActionMessage,
      tagPositionOptions,
      query,
      tagConfigs,
      visibleTagConfigs,
      areAllIndexedDisabled,
      disableAllIndexed,
      getValidateState,
      getTagConfigQuery,
      onExpandFormClick,
      onTagConfigEdit,
      onTagConfigToggleRemoved,
      setTagConfigAnimationStylesOnEnter,
      setTagConfigAnimationStylesOnLeave,
      removeTagConfigAnimationStyles,
    }
  },
})
